import { format } from 'date-fns';

export const formatSessionActivityMetaLine = (session, { showActivityStats = true } = {}) => {
  const dateStr = session.session_date
    ? format(new Date(session.session_date), 'EEE, MMM d')
    : '—';

  if (!showActivityStats) return dateStr;

  const games = session.games_count ?? 0;
  const players = session.players_count ?? 0;
  return `${dateStr} · ${games} game${games === 1 ? '' : 's'} · ${players} player${players === 1 ? '' : 's'}`;
};

export const getSessionStatusLabel = (status) => {
  switch (status) {
    case 'upcoming':
      return 'Open';
    case 'draft':
      return 'Draft';
    case 'ongoing':
      return 'Live';
    case 'completed':
      return 'Done';
    case 'archived':
      return 'Archived';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status || '—';
  }
};

export const getTodayDateString = () => {
  const today = new Date();
  return (
    today.getFullYear() +
    '-' +
    String(today.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(today.getDate()).padStart(2, '0')
  );
};

export const isSessionToday = (session) => {
  if (!session?.session_date) return false;
  const dateStr =
    typeof session.session_date === 'string'
      ? session.session_date.split('T')[0]
      : format(new Date(session.session_date), 'yyyy-MM-dd');
  return dateStr === getTodayDateString();
};

export const getSessionDateString = (session) => {
  if (!session?.session_date) return null;
  return typeof session.session_date === 'string'
    ? session.session_date.split('T')[0]
    : format(new Date(session.session_date), 'yyyy-MM-dd');
};

/** ISO-like sort key: session date + start time, most recent first when compared descending. */
export const getSessionRecencySortKey = (session) => {
  const dateStr = getSessionDateString(session) || '1970-01-01';
  const rawTime = session?.start_time || '00:00';
  const timePart = rawTime.includes('T')
    ? rawTime.split('T')[1]
    : rawTime.includes(' ')
      ? rawTime.split(' ')[1]
      : rawTime;
  const normalizedTime = timePart.length <= 5 ? `${timePart}:00` : timePart.split('.')[0];
  return `${dateStr}T${normalizedTime}`;
};

export const compareSessionsMostRecentFirst = (sessionA, sessionB) =>
  getSessionRecencySortKey(sessionB).localeCompare(getSessionRecencySortKey(sessionA));

const sortWorkItemsMostRecentFirst = (items) =>
  [...items].sort((a, b) => compareSessionsMostRecentFirst(a.session, b.session));

/** True when session_date is today or within the last `withinDays` calendar days. */
export const isWithinRecentDays = (session, withinDays = 7) => {
  const dateStr = getSessionDateString(session);
  if (!dateStr) return false;
  const sessionDate = new Date(`${dateStr}T12:00:00`);
  const cutoff = new Date();
  cutoff.setHours(12, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - withinDays);
  return sessionDate >= cutoff;
};

export const buildCompletedSessionAllMarked = (playersRows, paidSessionPlayerIds) => {
  const bySession = {};
  (playersRows || []).forEach((row) => {
    if (!bySession[row.session_id]) bySession[row.session_id] = [];
    bySession[row.session_id].push({
      ...row,
      session_payment: paidSessionPlayerIds.has(row.id) ? 'paid' : 'not_paid',
    });
  });

  const allMarked = {};
  Object.keys(bySession).forEach((sid) => {
    const players = bySession[sid] || [];
    allMarked[sid] =
      players.length === 0 ||
      players.every((p) => p.session_payment === 'paid' || p.exit_reason === 'no_show');
  });
  return allMarked;
};

/** Per-player session fee is set — wrap-up must mark paid or no-show before archive. */
export const sessionRequiresPaymentWrapUp = (session) => {
  const price = session?.price_per_player;
  return price != null && Number(price) > 0;
};

export const areSessionPlayersPaymentWrappedUp = (players = []) =>
  players.length === 0 ||
  players.every((sp) => sp.session_payment === 'paid' || sp.exit_reason === 'no_show');

/** True when payment step can be skipped or all players are paid / no-show. */
export const isSessionPaymentWrapUpComplete = (session, players = []) =>
  !sessionRequiresPaymentWrapUp(session) || areSessionPlayersPaymentWrappedUp(players);

export const sessionNeedsWrapUp = (session, completedSessionAllMarked) => {
  if (session?.session_status !== 'completed') return false;
  if (!sessionRequiresPaymentWrapUp(session)) return false;
  return completedSessionAllMarked[session.id] === false;
};

/** Completed sessions still awaiting payments and/or archive. */
export const sessionNeedsAdminFollowUp = (session) => session?.session_status === 'completed';

export const isCompetitiveSession = (session) =>
  session?.session_types?.name === 'Competitive Session';

/** Competitive completed session with no games — safe to delete when no payment records exist. Archived sessions are kept as records. */
export const canDeleteSession = (session, sessionsWithPayments = {}) => {
  if (!isCompetitiveSession(session)) return false;
  if (session.session_status !== 'completed') return false;
  if ((session.games_count ?? 0) !== 0) return false;
  if (sessionsWithPayments[session.id]) return false;
  return true;
};

export const categorizeSessionsByTime = (allSessions) => {
  const todayStr = getTodayDateString();
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekAgoStr =
    weekAgo.getFullYear() +
    '-' +
    String(weekAgo.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(weekAgo.getDate()).padStart(2, '0');

  const activeSessions = (allSessions || []).filter((s) => s.session_status !== 'archived');
  const archivedSessions = (allSessions || []).filter((s) => s.session_status === 'archived');

  return {
    today: activeSessions.filter((s) => {
      const d =
        typeof s.session_date === 'string'
          ? s.session_date.split('T')[0]
          : format(new Date(s.session_date), 'yyyy-MM-dd');
      return d === todayStr;
    }),
    thisWeek: activeSessions.filter((s) => {
      const d =
        typeof s.session_date === 'string'
          ? s.session_date.split('T')[0]
          : format(new Date(s.session_date), 'yyyy-MM-dd');
      return d >= weekAgoStr && d < todayStr;
    }),
    older: activeSessions.filter((s) => {
      const d =
        typeof s.session_date === 'string'
          ? s.session_date.split('T')[0]
          : format(new Date(s.session_date), 'yyyy-MM-dd');
      return d < weekAgoStr;
    }),
    archived: archivedSessions,
  };
};

/**
 * Build Today's Work inbox: host admin actions only.
 * - ongoing (live)
 * - upcoming scheduled for today (ready to start)
 * - completed not yet archived (payments and/or archive)
 * - competitive completed with 0 games (delete cleanup before archive)
 * Excludes archived sessions; excludes future upcoming, draft, cancelled.
 * Admin follow-up is capped with overflow to Past sessions.
 */
export const getTodayWorkItems = (
  sessions,
  completedSessionAllMarked,
  sessionsWithPayments = {},
  { maxAdminFollowUp = 5 } = {}
) => {
  const live = [];
  const startable = [];
  const deleteItems = [];
  const adminFollowUpCandidates = [];

  (sessions || []).forEach((session) => {
    if (session.session_status === 'ongoing') {
      live.push({
        type: 'live',
        session,
        label: session.name,
        action: 'enter',
        priority: 1,
      });
    } else if (session.session_status === 'upcoming' && isSessionToday(session)) {
      startable.push({
        type: 'startable',
        session,
        label: session.name,
        action: 'start',
        priority: 2,
      });
    } else if (canDeleteSession(session, sessionsWithPayments)) {
      deleteItems.push({
        type: 'delete',
        session,
        label: session.name,
        action: 'delete',
        priority: 3,
        sortDate: getSessionDateString(session) || '',
      });
    } else if (sessionNeedsAdminFollowUp(session)) {
      adminFollowUpCandidates.push({
        type: 'wrap-up',
        session,
        label: session.name,
        action: 'wrap-up',
        priority: 4,
        sortDate: getSessionDateString(session) || '',
        needsPayment:
          sessionRequiresPaymentWrapUp(session) &&
          completedSessionAllMarked[session.id] === false,
      });
    }
  });

  const sortedLive = sortWorkItemsMostRecentFirst(live);
  const sortedStartable = sortWorkItemsMostRecentFirst(startable);
  const sortedDeleteItems = sortWorkItemsMostRecentFirst(deleteItems);
  const sortedAdminFollowUpCandidates = sortWorkItemsMostRecentFirst(adminFollowUpCandidates);

  const overflowAdminFollowUpCount = Math.max(
    0,
    sortedAdminFollowUpCandidates.length - maxAdminFollowUp
  );
  const adminFollowUpShown = sortedAdminFollowUpCandidates
    .slice(0, maxAdminFollowUp)
    .map(({ sortDate, needsPayment, ...item }) => item);
  const deleteShown = sortedDeleteItems.map(({ sortDate, ...item }) => item);
  const adminFollowUp = sortWorkItemsMostRecentFirst([...deleteShown, ...adminFollowUpShown]);

  const items = [...sortedLive, ...sortedStartable, ...adminFollowUp];

  return {
    items,
    overflowWrapUpCount: overflowAdminFollowUpCount,
    totalWrapUpNeedingAttention: adminFollowUpCandidates.length,
  };
};

export const filterUpcomingSessions = (sessions) =>
  (sessions || []).filter(
    (s) => s.session_status === 'draft' || s.session_status === 'upcoming'
  );

/** Planned session (upcoming or draft) whose date has passed but it was never started. */
export const isMissedPlannedSession = (session) => {
  if (session?.session_status !== 'upcoming' && session?.session_status !== 'draft') return false;
  const dateStr = getSessionDateString(session);
  if (!dateStr) return false;
  return dateStr < getTodayDateString();
};

export const countMissedPlannedSessions = (sessions) =>
  (sessions || []).filter(isMissedPlannedSession).length;

/** Soonest planned session (upcoming or draft) dated today or later; excludes missed and undated sessions. */
export const getNextPlannedSession = (sessions) => {
  const todayStr = getTodayDateString();
  const candidates = (sessions || []).filter((session) => {
    if (session?.session_status !== 'upcoming' && session?.session_status !== 'draft') return false;
    const dateStr = getSessionDateString(session);
    return Boolean(dateStr) && dateStr >= todayStr;
  });
  if (!candidates.length) return null;
  return candidates.reduce((soonest, session) =>
    getSessionRecencySortKey(session) < getSessionRecencySortKey(soonest) ? session : soonest
  );
};

/** Session IDs already shown in the Today's work inbox — hide from Past "today" when embedded. */
export const getTodayWorkInboxSessionIds = (items) =>
  new Set((items || []).map((item) => item.session?.id).filter(Boolean));

/** Past sessions hub: played or finished lifecycle only — not open planning rows. */
export const isHubPastSessionListItem = (session) =>
  session?.session_status !== 'upcoming' && session?.session_status !== 'draft';
